import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type TradeSource = 'insider' | 'congress';

export interface SavedTrade {
  trade_id: string;
  trade_source: TradeSource;
  saved_at: string;
}

const FREE_LIMIT = 10;
const PRO_LIMIT = 100;

export type SaveResult = 'saved' | 'unsaved' | 'limit' | 'unauthenticated' | 'error';

interface SavedTradesContextValue {
  savedTrades: SavedTrade[];
  loading: boolean;
  count: number;
  freeLimit: number;
  proLimit: number;
  isSaved: (tradeId: string, source: TradeSource) => boolean;
  save: (tradeId: string, source: TradeSource) => Promise<SaveResult>;
  unsave: (tradeId: string, source: TradeSource) => Promise<SaveResult>;
  refresh: () => Promise<void>;
}

const SavedTradesContext = createContext<SavedTradesContextValue | null>(null);

export const SavedTradesProvider = ({ children }: { children: ReactNode }) => {
  const { user, isPro } = useAuth();
  const [savedTrades, setSavedTrades] = useState<SavedTrade[]>([]);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);

  const fetchSaved = useCallback(async () => {
    if (!user) {
      setSavedTrades([]);
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_saved_trades')
        .select('trade_id, trade_source, saved_at')
        .eq('user_id', user.id);
      if (error) throw error;
      setSavedTrades(
        (data || []).map((r) => ({
          trade_id: r.trade_id,
          trade_source: r.trade_source as TradeSource,
          saved_at: r.saved_at,
        }))
      );
    } catch (err) {
      console.error('Failed to fetch saved trades:', err);
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [user]);

  useEffect(() => {
    fetchSaved();
  }, [fetchSaved]);

  const isSaved = useCallback(
    (tradeId: string, source: TradeSource) =>
      savedTrades.some((t) => t.trade_id === tradeId && t.trade_source === source),
    [savedTrades]
  );

  const save = useCallback(
    async (tradeId: string, source: TradeSource): Promise<SaveResult> => {
      if (!user) return 'unauthenticated';
      if (!tradeId) return 'error';
      if (isSaved(tradeId, source)) return 'saved';

      const cap = isPro ? PRO_LIMIT : FREE_LIMIT;
      if (savedTrades.length >= cap) {
        if (!isPro) {
          toast(`You've saved ${FREE_LIMIT} trades. Pro increases this to ${PRO_LIMIT}.`, {
            action: { label: 'Upgrade', onClick: () => { window.location.href = '/pricing'; } },
          });
        }
        return 'limit';
      }

      const optimistic: SavedTrade = {
        trade_id: tradeId,
        trade_source: source,
        saved_at: new Date().toISOString(),
      };
      setSavedTrades((prev) => [...prev, optimistic]);

      const { error } = await supabase
        .from('user_saved_trades')
        .insert({ user_id: user.id, trade_id: tradeId, trade_source: source });

      if (error) {
        setSavedTrades((prev) =>
          prev.filter((t) => !(t.trade_id === tradeId && t.trade_source === source))
        );
        console.error('Failed to save trade:', error);
        return 'error';
      }
      return 'saved';
    },
    [user, isPro, isSaved, savedTrades.length]
  );

  const unsave = useCallback(
    async (tradeId: string, source: TradeSource): Promise<SaveResult> => {
      if (!user) return 'unauthenticated';
      if (!tradeId) return 'error';

      const prev = savedTrades;
      setSavedTrades((cur) =>
        cur.filter((t) => !(t.trade_id === tradeId && t.trade_source === source))
      );

      const { error } = await supabase
        .from('user_saved_trades')
        .delete()
        .eq('user_id', user.id)
        .eq('trade_id', tradeId)
        .eq('trade_source', source);

      if (error) {
        setSavedTrades(prev);
        console.error('Failed to unsave trade:', error);
        return 'error';
      }
      return 'unsaved';
    },
    [user, savedTrades]
  );

  return (
    <SavedTradesContext.Provider
      value={{
        savedTrades,
        loading,
        count: savedTrades.length,
        freeLimit: FREE_LIMIT,
        proLimit: PRO_LIMIT,
        isSaved,
        save,
        unsave,
        refresh: fetchSaved,
      }}
    >
      {children}
    </SavedTradesContext.Provider>
  );
};

export const useSavedTradesContext = () => {
  const ctx = useContext(SavedTradesContext);
  if (!ctx) {
    throw new Error('useSavedTradesContext must be used within SavedTradesProvider');
  }
  return ctx;
};
